import { useEffect, useState, type ReactNode } from "react";
import { PowerSyncContext } from "@powersync/react";
import type { PowerSyncDatabase } from "@powersync/web";
import { openDatabase, closeDatabase, type DataMode } from "@/data/db";
import { DEMO_USER_ID } from "@/data/demo";
import { loadSession, signOut as endSession } from "@/auth/session";
import { SignIn } from "@/features/signin/SignIn";
import { Splash } from "@/ui/Splash";

// Chooses the data mode, opens the matching local database and exposes it.
// live: a real account on the configured backend (dev by default)
// demo: the local fictional team, never synced
export interface BootState { mode: DataMode; userId: string; email: string; db: PowerSyncDatabase; signOut: () => void }

const MODE_KEY = "pm.mode";

export function Boot({ children }: { children: (s: BootState) => ReactNode }) {
  const [mode, setMode] = useState<DataMode | null>(() => {
    const m = safeGet(MODE_KEY);
    if (m === "demo") return "demo";
    return loadSession() ? "live" : null;
  });
  const [db, setDb] = useState<PowerSyncDatabase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const session = loadSession();

  useEffect(() => {
    if (!mode) return;
    let alive = true;
    setDb(null);
    openDatabase(mode, mode === "live" ? session?.user.id : DEMO_USER_ID)
      .then((d) => alive && setDb(d))
      .catch((e) => alive && setError(String(e?.message ?? e)));
    return () => { alive = false; };
  }, [mode, session?.user.id]);

  const signOut = async () => {
    safeSet(MODE_KEY, null);
    await closeDatabase(true);
    await endSession();
    setDb(null);
    setMode(null);
  };

  if (!mode) {
    return <SignIn onSignedIn={() => { safeSet(MODE_KEY, "live"); setMode("live"); }} onDemo={() => { safeSet(MODE_KEY, "demo"); setMode("demo"); }} />;
  }
  if (error) return <Splash title="Local database unavailable" detail={error} onRetry={() => location.reload()} onSignOut={signOut} />;
  if (!db) return <Splash title={mode === "demo" ? "Loading the demo team" : "Syncing field data"} />;

  return (
    <PowerSyncContext.Provider value={db}>
      {children({ mode, db, userId: mode === "demo" ? DEMO_USER_ID : session!.user.id, email: mode === "demo" ? "avery@prairieridge.example" : session!.user.email, signOut })}
    </PowerSyncContext.Provider>
  );
}

function safeGet(k: string) { try { return localStorage.getItem(k); } catch { return null; } }
function safeSet(k: string, v: string | null) { try { if (v) localStorage.setItem(k, v); else localStorage.removeItem(k); } catch { /* ignore */ } }
